/**
 * Pure mappers between the CDP experiences edge (`/e/v1/pending`) and the on-site
 * overlay view models. No network here — unit-tested in isolation (mirrors the
 * game-host's `game-mapper.ts`).
 *
 * The overlay renders only two kinds of experience: a plain **popup** (title/body/
 * CTA) and a **survey** popup (`content.kind==='survey'` with a server-hydrated
 * `content.survey` struct + `content.response_token`). Everything else the edge
 * returns — games (`render_type='game'`, handled by the game-host), chat, recs,
 * inline widgets, unrendered surveys — is skipped so the two pipelines don't fight.
 */

import type { PendingExperience } from "./experiences-client";

/** render_types the overlay treats as a modal popup. */
const POPUP_RENDER_TYPES = new Set(["popup", "slide_in", "sheet"]);
/** Для попапов из блоков — ещё и полоса (bar): у неё тоже готовый content.html. */
const HTML_RENDER_TYPES = new Set(["popup", "slide_in", "sheet", "bar"]);

export interface CtaView {
  label: string;
  action?: "open_chat";
  url: string | null;
}

export interface ClientRulesView {
  display?: { type?: string; delay_seconds?: number };
  dismiss_cooldown_seconds?: number;
  consent_topic?: string;
  [key: string]: unknown;
}

export interface PopupView {
  kind: "popup";
  presentation?: "modal" | "slide_in";
  experienceId: string;
  assignmentId: string | null;
  assignmentReceipt: string | null;
  surface: string;
  title: string | null;
  body: string | null;
  image: string | null;
  cta: CtaView | null;
  clientRules: ClientRulesView | null;
  /** Optional promo code to surface (offer popups). */
  code: string | null;
  primary: string | null;
  /** Тестовый показ по ссылке (события не записываются, на карточке — пометка). */
  preview: boolean;
}

/**
 * Попап, собранный в конструкторе блоков (content.kind='blocks'): сервер уже отрисовал и
 * санитизировал content.html. Раньше витрина такие попапы молча пропускала (рисовала только
 * title/body) — баг-лист Насти п.1: её попап из блоков не появлялся на сайте вовсе.
 */
export interface HtmlPopupView {
  kind: "html";
  presentation?: "modal" | "slide_in";
  experienceId: string;
  assignmentId: string | null;
  assignmentReceipt: string | null;
  surface: string;
  html: string;
  clientRules: ClientRulesView | null;
  primary: string | null;
  preview: boolean;
}

export type SurveyScaleType = "nps" | "csat" | "ces" | "thumbs";

export interface SurveyExtraQuestion {
  id: string;
  type: "stars" | "text" | "single_choice" | "multi_choice";
  text: string;
  options: string[];
  required: boolean;
}

export interface SurveyView {
  kind: "survey";
  experienceId: string;
  assignmentId: string | null;
  assignmentReceipt: string | null;
  surface: string;
  responseToken: string;
  name: string | null;
  question: string;
  scaleType: SurveyScaleType;
  askComment: boolean;
  extraQuestions: SurveyExtraQuestion[];
  thanksTitle: string;
  primary: string | null;
}

export type RenderableExperience = PopupView | SurveyView | HtmlPopupView;

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim();
}

function readCta(v: unknown): CtaView | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const label = str(o.label);
  if (!label) return null;
  // Keep the edge-safe wings:// action; arbitrary action strings are never executed.
  const action = o.action === "wings://open-chat" ? "open_chat" : null;
  const url = str(o.url) || str(o.href);
  return { label, ...(action ? { action } : {}), url: url || null };
}

function readPrimary(content: Record<string, unknown>): string | null {
  const theme = content.theme;
  if (theme && typeof theme === "object") {
    const p = str((theme as Record<string, unknown>).primary) || str((theme as Record<string, unknown>).accent);
    if (p) return p;
  }
  const tc = str(content.theme_color) || str(content.accent);
  return tc || null;
}

const VALID_SCALES: ReadonlySet<string> = new Set(["nps", "csat", "ces", "thumbs"]);

/** Parse the extra questions carried in `survey.content.questions` (B16 multi-question). */
export function readSurveyExtraQuestions(surveyContent: unknown): SurveyExtraQuestion[] {
  if (!surveyContent || typeof surveyContent !== "object") return [];
  const raw = (surveyContent as Record<string, unknown>).questions;
  if (!Array.isArray(raw)) return [];
  const out: SurveyExtraQuestion[] = [];
  for (const q of raw) {
    if (!q || typeof q !== "object") continue;
    const o = q as Record<string, unknown>;
    const id = str(o.id);
    const type = str(o.type);
    const text = str(o.text);
    if (!id || !text) continue;
    if (type !== "stars" && type !== "text" && type !== "single_choice" && type !== "multi_choice") continue;
    const options = Array.isArray(o.options) ? o.options.map(str).filter(Boolean) : [];
    if ((type === "single_choice" || type === "multi_choice") && options.length === 0) continue;
    out.push({ id, type, text, options, required: o.required === true });
  }
  return out;
}

/** Map one pending experience to a renderable overlay model, or null if the overlay skips it. */
export function toRenderable(p: PendingExperience): RenderableExperience | null {
  const content = (p.content ?? {}) as Record<string, unknown>;

  // Survey popup — discriminated by content.kind, needs a hydrated survey + token.
  if (content.kind === "survey") {
    const survey = content.survey;
    const token = str(content.response_token);
    if (!survey || typeof survey !== "object" || !token) return null;
    const s = survey as Record<string, unknown>;
    const question = str(s.question);
    if (!question) return null;
    const scale = str(s.scale_type);
    const scaleType = (VALID_SCALES.has(scale) ? scale : "csat") as SurveyScaleType;
    const sContent = (s.content ?? {}) as Record<string, unknown>;
    return {
      kind: "survey",
      experienceId: p.experience_id,
      assignmentId: p.assignment_id ?? null,
      assignmentReceipt: p.assignment_receipt ?? null,
      surface: p.surface ?? "web",
      responseToken: token,
      name: str(s.name) || null,
      question,
      scaleType,
      askComment: s.ask_comment === true,
      extraQuestions: readSurveyExtraQuestions(sContent),
      thanksTitle: str(sContent.thanks_title) || "Спасибо за отзыв!",
      primary: readPrimary(sContent) || readPrimary(content),
    };
  }

  // Попап из конструктора блоков — готовый санитизированный сервером HTML.
  if (HTML_RENDER_TYPES.has(p.render_type) && content.kind === "blocks") {
    const html = typeof content.html === "string" ? content.html.trim() : "";
    if (!html) return null;
    return {
      kind: "html",
      presentation: p.render_type === "slide_in" ? "slide_in" : "modal",
      experienceId: p.experience_id,
      assignmentId: p.assignment_id ?? null,
      assignmentReceipt: p.assignment_receipt ?? null,
      surface: p.surface ?? "web",
      html,
      clientRules: p.client_rules && typeof p.client_rules === "object" ? p.client_rules : null,
      primary: readPrimary(content),
      preview: p.preview === true,
    };
  }

  // Plain popup — only popup-family render types with some body.
  if (POPUP_RENDER_TYPES.has(p.render_type)) {
    const title = str(content.title) || null;
    const body = str(content.body) || str(content.text) || null;
    if (!title && !body) return null;
    return {
      kind: "popup",
      presentation: p.render_type === "slide_in" ? "slide_in" : "modal",
      experienceId: p.experience_id,
      assignmentId: p.assignment_id ?? null,
      assignmentReceipt: p.assignment_receipt ?? null,
      surface: p.surface ?? "web",
      title,
      body,
      image: str(content.image) || null,
      cta: readCta(content.cta),
      clientRules: p.client_rules && typeof p.client_rules === "object" ? p.client_rules : null,
      code: str(content.code) || null,
      primary: readPrimary(content),
      preview: p.preview === true,
    };
  }

  // game / chat / recs_widget / inapp_card / inline_block / tooltip / … → not ours.
  return null;
}

/** First renderable experience from a pending list (the edge already orders by priority). */
export function pickRenderable(list: PendingExperience[]): RenderableExperience | null {
  for (const p of list) {
    const r = toRenderable(p);
    if (r) return r;
  }
  return null;
}

/**
 * Score choices to render for a survey head question, derived from its scale.
 * csat/ces → 1–5, nps → 0–10, thumbs → 👎(0)/👍(1). The submit `score` is the raw
 * value the engine validates against the scale (`POST /s/<code>` body `{score}`).
 */
export function surveyScoreOptions(scale: SurveyScaleType): Array<{ value: number; label: string }> {
  if (scale === "thumbs") {
    return [
      { value: 0, label: "👎" },
      { value: 1, label: "👍" },
    ];
  }
  const min = scale === "nps" ? 0 : 1;
  const max = scale === "nps" ? 10 : 5;
  const out: Array<{ value: number; label: string }> = [];
  for (let v = min; v <= max; v++) out.push({ value: v, label: String(v) });
  return out;
}
